import { useCallback, useRef, useState } from "react";
import { askInConversation } from "../ask-in-conversation";
import {
  askQuestion,
  conversationTurns,
  listRecentConversations,
  sendFeedback,
  startConversation,
} from "../assistant-api";
import type { ChatTurn, ConversationSummary, QuestionImage, Vote } from "../assistant-types";
import { settledFields, turnsFromViews } from "../chat-turns";

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/** One Conversation with the Support Bot, asked as `callerUsername`, plus the Caller's recent ones to resume. */
export function useAssistantChat(callerUsername: string | undefined) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [sending, setSending] = useState(false);
  /** Undefined until loaded. */
  const [recent, setRecent] = useState<ConversationSummary[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const conversationId = useRef<string | undefined>(undefined);

  const updateTurn = useCallback((id: string, change: Partial<ChatTurn>) => {
    setTurns((current) => current.map((turn) => (turn.id === id ? { ...turn, ...change } : turn)));
  }, []);

  const reset = useCallback(() => {
    conversationId.current = undefined;
    setTurns([]);
    setRecent(undefined);
  }, []);

  const loadRecent = useCallback(async () => {
    setLoadError(undefined);
    try {
      setRecent(await listRecentConversations(callerUsername));
    } catch (error) {
      setRecent([]);
      setLoadError(`Couldn't load your recent conversations: ${errorText(error)}`);
    }
  }, [callerUsername]);

  const resume = useCallback(
    async (id: string) => {
      setLoadError(undefined);
      try {
        const views = await conversationTurns(id, callerUsername);
        conversationId.current = id;
        setTurns(turnsFromViews(views));
      } catch (error) {
        setLoadError(`Couldn't open that conversation: ${errorText(error)}`);
      }
    },
    [callerUsername],
  );

  const ask = useCallback(
    async (text: string, images: QuestionImage[]) => {
      const turn: ChatTurn = {
        id: crypto.randomUUID(),
        question: text,
        imageCount: images.length,
        status: "pending",
      };
      setTurns((current) => [...current, turn]);
      setSending(true);
      const startedAt = performance.now();
      try {
        const asked = await askInConversation(conversationId.current, {
          start: () => startConversation(callerUsername),
          ask: (id) => askQuestion(id, callerUsername, { text, images }),
        });
        conversationId.current = asked.conversationId;
        updateTurn(turn.id, {
          ...settledFields(asked.settled),
          elapsedMs: performance.now() - startedAt,
        });
      } catch (error) {
        updateTurn(turn.id, { status: "failed", error: errorText(error) });
      } finally {
        setSending(false);
      }
    },
    [callerUsername, updateTurn],
  );

  const rate = useCallback(
    async (turn: ChatTurn, vote: Vote) => {
      if (turn.questionId === undefined) return;
      updateTurn(turn.id, { vote });
      await sendFeedback(turn.questionId, callerUsername, vote).catch((error: unknown) =>
        updateTurn(turn.id, { vote: undefined, error: `Feedback not saved: ${errorText(error)}` }),
      );
    },
    [callerUsername, updateTurn],
  );

  return { turns, sending, ask, rate, reset, recent, loadRecent, resume, loadError };
}
